import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Subject, catchError, map, of, take, takeUntil, tap } from 'rxjs';

import { CookieService } from 'ngx-cookie-service';
import { AuthService } from 'src/app/services/auth.service';
import { LocaleService } from 'src/app/services/locale.service';
import { MessageService } from 'src/app/services/message.service';
import { UserProfileService } from 'src/app/services/user-profile.service';

import { HttpEvent, HttpEventType } from '@angular/common/http';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { Constants } from 'src/app/shared/constants';
import { LocalePipe } from 'src/app/shared/pipes/locale.pipe';
import { User } from 'src/app/types/user';
import { UserSettings } from 'src/app/types/user-settings';
import { environment } from 'src/environments/environment';

@Component( {
    selector: 'app-profile',
    templateUrl: './profile.component.html',
    styleUrls: ['./profile.component.scss'],
    standalone: false
} )
export class ProfileComponent implements OnInit, OnDestroy {
    /** user object */
    account?: User | null;

    /** flag if user has a signature */
    hasSignature$: BehaviorSubject<boolean> = new BehaviorSubject( false );
    signature?: string;
    progress = 0;

    profileTab = $localize`:@@tab-label-profil:Profile`;
    preferencesTab = $localize`:@@tab-label-preferences:Preferences`;
    signatureTab = $localize`:@@tab-label-signature:Signature`;
    nophone = $localize`:@@profil-nophone:No phone number provided`;

    headline = $localize`:@@profile-headline:User profile`;
    /** get filled after organization details are retrieved */
    subline = '';

    /** date, to be shown in format locale hint */
    date = new Date();
    /** number, to be shown in format locale hint */
    num = 1234.56;
    /** currency, to be shown in format locale hint */
    amount = 4567.89;
    currency = signal( '$4567.89' );

    /** supported languages and locales as constants */
    languages = Constants.supportedLanguages;
    sortedLanguages: string[][] = [];

    formatLocales = Constants.supportedLocales;
    sortedFormatLocales: string[][] = [];

    selectedTab = 0;

    /** profile form group */
    profileForm = new FormGroup( {
        avatarURL: new FormControl( '' ),
        preferredLanguage: new FormControl( '', [Validators.required] ),
        formatLocale: new FormControl( '', [Validators.required] )
    } );

    /**  lifecycle hook */
    readonly #destroyed: Subject<void> = new Subject<void>();

    #authService = inject( AuthService );
    #userProfileService = inject( UserProfileService );
    #localePipe = inject( LocalePipe );
    #localeService = inject( LocaleService );
    #localStorageService = inject( LocalStorageService );
    #messageService = inject( MessageService );
    #cookieService = inject( CookieService );
    #router = inject( Router );

    europe = $localize`:@@profile-region-europe:Europe`;
    asia = $localize`:@@profile-region-asia:Asia`;

    regions = [
        ['EUROPE', this.europe],
        ['ASIA', this.asia]
    ];
    selectedRegion = 'EUROPE';
    showHostRegions = environment.optionalModules.showHostRegions;
    supportedRegions = this.#cookieService.get( 'scope.regions' ).split( ',' );

    ngOnInit(): void {
        /**
         * observe router changes for NavigationEnd event
         * this is triggered {@link ProfileComponent#changeSettings},
         * when language in user profile had been changed
         */
        this.#router.events.pipe( takeUntil( this.#destroyed ) ).subscribe( ( event ) => {
            if ( event instanceof NavigationEnd ) {
                window.location.reload();
            }
        } );

        /** load account data from storage */
        this.account = this.#localStorageService.load( 'account' );

        /** set last opened tab after reload */
        const lastTab = this.#localStorageService.load( 'profile.tab' ) || 0;
        if ( lastTab && lastTab > 0 ) this.onTabChange( lastTab );

        /** set selected region after reload */
        this.selectedRegion = this.#cookieService.get( 'scope.region' ) || 'EUROPE';

        /** then set the user interface */
        this.setUserProfile();
    }

    /**
     * set user profile form
     */
    setUserProfile() {
        const { avatarURL, preferredLanguage, formatLocale } = this.account!;

        // update form values
        this.profileForm.patchValue( {
            avatarURL,
            preferredLanguage,
            formatLocale
        } );

        /**
         * not using nullish coalescing operator
         * as a nullish value is not acceptable here
         * instead we want to make sure locale is set
         */
        const locale = formatLocale ?? 'en-US';
        this.changeCurrencyLocale( locale );

        const collator = new Intl.Collator( locale );

        // create tuple of languages
        this.languages.forEach( ( key ) => {
            const lang = this.#localePipe.transform( key );
            const arr = [key, lang];
            this.sortedLanguages.push( arr );
        } );

        // create tuple of locales
        this.formatLocales.forEach( ( key ) => {
            const lang = this.#localePipe.transform( key );
            const arr = [key, lang];
            this.sortedFormatLocales.push( arr );
        } );

        // sort tuples according to current language
        this.sortedLanguages.sort( ( a, b ) => collator.compare( a[1], b[1] ) );
        this.sortedFormatLocales.sort( ( a, b ) => collator.compare( a[1], b[1] ) );
    }

    /**
     * update user profile in backend
     * and show message if successful
     */
    updateUserProfile() {
        if ( this.profileForm.invalid ) {
            return;
        }

        const formValues = this.profileForm.value;
        const settings = {
            avatarURL: formValues.avatarURL,
            preferredLanguage: formValues.preferredLanguage,
            formatLocale: formValues.formatLocale
        } as UserSettings;

        this.#userProfileService
            .updateUserProfile( this.account!.OID, settings )
            .pipe(
                take( 1 ),
                tap( () => {
                    /** show success message */
                    const msg = {
                        text: $localize`:@@success-msg-profile-text:Your changes are successfully saved.`,
                        action: $localize`:@@success-msg-profile-action:OK`,
                    };
                    this.#messageService.success( msg );

                    this.changeSettings( settings );
                } ),
                catchError( () => {
                    /** show error message */
                    const msg = {
                        text: $localize`:@@error-msg-profile-text:Please try saving your changes again. If it is not working contact our support team.`,
                        action: $localize`:@@error-msg-profile-action:Try again`
                    };
                    this.#messageService.error( msg );
                    return of();
                } )
            )
            .subscribe();
    }

    /**
     * helper function to load user signature from api
     * as api returns a blob, which is converted to base64 image data string
     */
    getUserSignature() {
        this.#userProfileService
            .getUserSignature( this.account!.OID )
            .pipe(
                take( 1 ),
                map( ( res ) => {
                    const reader = new FileReader();
                    reader.readAsDataURL( res );
                    reader.onloadend = () => {
                        // result includes identifier 'data:image/png;base64,' and the base64 data
                        this.signature = reader.result as string;
                        this.hasSignature$.next( true );
                    };
                } ),
                catchError( async ( error ) => console.log( error ) )
            )
            .subscribe();
    }

    /**
     * helper function to upload one or more files via api
     * @param {Array} $files - list of files
     */
    uploadUserSignature( $files: File[] ): void {
        const msg = {
            text: $localize`:@@success-msg-signature-text:Your signature has been successfully uploaded.`,
            action: $localize`:@@success-msg-signature-action:OK`,
        };
        const err = {
            text: $localize`:@@error-msg-signature-text:Please try again. If it is not working contact our support team.`,
            action: $localize`:@@error-msg-signature-action:Try again`
        };

        this.#userProfileService
            .uploadUserSignature( this.account!.OID, $files )
            .pipe(
                takeUntil( this.#destroyed ),
                catchError( () => {
                    this.#messageService.error( err );
                    return of();
                } )
            )
            .subscribe( ( event: HttpEvent<any> ) => {
                switch ( event.type ) {
                    case HttpEventType.UploadProgress:
                        this.progress = Math.round( ( event.loaded / event.total! ) * 100 );
                        break;
                    case HttpEventType.Response:
                        // unset progress
                        this.progress = 0;
                        // load signature from server and show it on screen
                        this.getUserSignature();
                        // show success message
                        this.#messageService.success( msg );
                }
            } );
    }

    /**
     * helper function to delete uploaded signature from server
     */
    deleteSignature() {
        this.#userProfileService
            .deleteUserSignature( this.account!.OID )
            .pipe( take( 1 ) )
            .subscribe( () => this.hasSignature$.next( false ) );
    }

    /**
     * helper function to update avatar emoji display
     * @param {string} avatar - name of avatar
     */
    changeAvatar( avatar: string ) {
        this.profileForm.patchValue( {
            avatarURL: avatar
        } );
    }

    /**
     * helper function to update format locale hint
     * @param {string} locale - locale as string
     */
    changeCurrencyLocale( locale: string ) {
        if ( locale && locale !== '' ) {
            const currencySymbol = new Intl.NumberFormat( locale, { style: 'currency', currency: 'EUR' } ).format(
                this.amount
            );
            this.currency.set( currencySymbol );
        }
    }

    /**
     * distribute changed settings when stored in backend
     * - updates account by auth service
     * - sets locale_id in app
     * - sets language in app
     *
     * example: {
     *      avatarURL: 'avatar_placeholder',
     *      preferredLanguage: 'en-US',
     *      formatLocale: 'de-DE'
     * }
     * @param settings {object} - see example above
     */
    changeSettings( settings: UserSettings ) {
        this.#authService.updateAccount( settings );

        const lang = settings.preferredLanguage;
        const languageChanged = this.account!.preferredLanguage !== lang;
        const formatChanged = this.account!.formatLocale !== settings.formatLocale;

        if ( formatChanged && settings.formatLocale ) {
            this.#localeService.setLocale( settings.formatLocale );
        }
        /**
         * when language changed in profile
         * store language as lang in cookie
         * and navigate to same url to trigger reload
         * which is observed {@link ProfileComponent#ngOnInit}
         */
        if ( languageChanged && lang ) {
            this.#cookieService.set( 'language', lang.slice( 0, 2 ), undefined, '/' );
            this.#router.navigate( [this.#router.url] );
        }
    }

    /**
     * helper function to store last opened tab
     * @param {number} selectedTabIndex index of last opened tab
     */
    onTabChange( selectedTabIndex: number ): void {
        this.selectedTab = selectedTabIndex;
        this.#localStorageService.save( 'profile.tab', this.selectedTab );

        // get signature file when tab content is shown
        if ( selectedTabIndex === 2 ) this.getUserSignature();
    }

    changeRegion( region: string ) {
        const expireDate = new Date();

        // As of Chrome release M104 (August 2022) cookies can no longer
        // set an expiration date more than 400 days in the future.
        const currentDate = expireDate.getDate();
        expireDate.setDate( currentDate + 400 );

        this.#cookieService.set( 'scope.region', region, expireDate );

        /** show success message */
        const msg = {
            text: $localize`:@@success-msg-profile-region-text:Your region has been changed.`,
            action: $localize`:@@success-msg-profile-region-action:OK`,
        };
        this.#messageService.success( msg );
    }

    ngOnDestroy() {
        /** remove tab cookie when leaving view to reset initial/first tab */
        this.#localStorageService.delete( 'profile.tab' );

        this.#destroyed.next();
        this.#destroyed.complete();
    }
}
